import type * as React from 'react';

import { OrganizationFeature } from '@gitbook/api-client';
import {
    ALL_CONTENT_PERMISSIONS,
    type DefaultPermissionLevel,
    GUEST_ROLE_LABEL,
    type OrganizationMemberRoleOrGuest,
    MEMBER_ROLE_LABELS,
} from '@gitbook/app-database';
import {
    createPalette,
    PaletteButton,
    PaletteCommandType,
    type PaletteGroupDef,
} from '@gitbook/react-uikit';
import { Icon, type SVGIconComponent } from '@gitbook/spine-icons';
import type { ButtonProps, IconDefinition } from '@gitbook/spine-react';
import { filterOutNullable } from '@gitbook/util-ts';

import {
    useTrialingOrganizationFeaturePlan,
    useCurrentOrganization,
    useGetUpgradeAction,
} from '../../state';
import { getTrialingBadgeProps } from '../OrganizationBilling';

interface PermissionRoleDescriptor {
    title: string;
    description: string;
    icon: SVGIconComponent;
    needAdvancedPermissions?: boolean;
}

type PermissionRoleOption = Exclude<DefaultPermissionLevel, undefined>;

export interface PermissionRoleGroup {
    key: string;
    title?: string;
    roles: PermissionRoleOption[];
}

/**
 * A permissions dictionnary exposing a title, description, and icon for each permission.
 */
export const PERMISSION_ROLES_DESCRIPTORS: {
    [key in Exclude<DefaultPermissionLevel, null>]: PermissionRoleDescriptor;
} = {
    inherit: {
        title: 'Inherited',
        description: 'Match roles set in the Organization',
        icon: Icon.RoleInherit,
    },
    admin: {
        ...MEMBER_ROLE_LABELS.admin,
        icon: Icon.RoleAdmin,
    },
    create: {
        ...MEMBER_ROLE_LABELS.create,
        icon: Icon.RoleCreator,
    },
    review: {
        ...MEMBER_ROLE_LABELS.review,
        icon: Icon.RoleReviewer,
        needAdvancedPermissions: true,
    },
    edit: {
        ...MEMBER_ROLE_LABELS.edit,
        icon: Icon.RoleEditor,
    },
    comment: {
        ...MEMBER_ROLE_LABELS.comment,
        icon: Icon.RoleCommenter,
        needAdvancedPermissions: true,
    },
    read: {
        ...MEMBER_ROLE_LABELS.read,
        icon: Icon.RoleReader,
    },
};

export const PERMISSION_GUEST_ROLE_DESCRIPTOR: PermissionRoleDescriptor = {
    ...GUEST_ROLE_LABEL,
    icon: Icon.PermissionNone,
};

export const PERMISSION_NO_ACCESS_ROLE_DESCRIPTOR: PermissionRoleDescriptor = {
    title: 'Deny access',
    description: 'No access',
    icon: Icon.PermissionNone,
};

type PermissionRoleButtonProps<Role> = {
    /** Mode to use to display the definition of the roles */
    mode?: 'member' | 'content';
    /** Current role, if not specified, no value is shown as selected */
    value?: Role | undefined;
    /** Callback when a new role is seelcted */
    onValueChange: (newValue: Role) => void;
    /** Optional callback to allow deleting the value */
    onRemove?: () => void;
    /** Label for the remove command in the palette. Defaults to "Remove". */
    removeLabel?: string;
    /** Optional to disable the listing of no access */
    allowNullValue?: boolean;
    /** Optional to allow the inherit value */
    allowInherit?: boolean;
    /** Icon for the button, if passed null it doesn't show the default icon */
    icon?: IconDefinition | null;
    /** Transform the palette */
    extraPaletteGroups?: () => PaletteGroupDef[];
    /** Optional custom grouping of roles in the palette */
    roleGroups?: PermissionRoleGroup[];
    /** Optional label override for the button */
    label?: string;
} & Pick<ButtonProps, 'trailingIcon' | 'size' | 'kind' | 'disabled' | 'className' | 'labelStyle'>;

/**
 * Button to select a role.
 */
export function PermissionRoleButton(
    props:
        | (PermissionRoleButtonProps<OrganizationMemberRoleOrGuest> & {
              allowInherit?: false;
          })
        | (PermissionRoleButtonProps<DefaultPermissionLevel> & { allowInherit: true })
): React.ReactElement {
    const {
        mode,
        value,
        onValueChange,
        onRemove,
        removeLabel = 'Remove',
        icon,
        trailingIcon,
        allowNullValue = true,
        allowInherit = false,
        extraPaletteGroups,
        roleGroups,
        label,
        ...buttonProps
    } = props;
    const organization = useCurrentOrganization();

    const upgradeForAdvancedPermissions = useGetUpgradeAction({
        organization,
        featureKey: OrganizationFeature.MembersAdvancedRoles,
    });
    const trialingPlan = useTrialingOrganizationFeaturePlan(
        OrganizationFeature.MembersAdvancedRoles
    );

    const nullDescriptor =
        mode === 'member' ? PERMISSION_GUEST_ROLE_DESCRIPTOR : PERMISSION_NO_ACCESS_ROLE_DESCRIPTOR;
    const currentDescriptor =
        value === undefined
            ? undefined
            : value === null
              ? nullDescriptor
              : PERMISSION_ROLES_DESCRIPTORS[value];

    // The no-access option reads as an action in the menu ("Deny access") but as a state on the
    // selected button ("No access"). Guest mode keeps its own label.
    const selectedRoleLabel =
        value === null && mode !== 'member' ? nullDescriptor.description : currentDescriptor?.title;

    const palette = () => {
        const defaultRoles: PermissionRoleOption[] = [
            ...(allowInherit ? (['inherit'] as const) : []),
            ...ALL_CONTENT_PERMISSIONS,
        ];
        const effectiveRoleGroups = roleGroups ?? [{ key: 'roles', roles: defaultRoles }];

        return createPalette({
            groups: [
                ...(extraPaletteGroups ? extraPaletteGroups() : []),
                ...effectiveRoleGroups
                    .map((group) => {
                        const commands = group.roles
                            .map((role) => {
                                if (role === null && !allowNullValue) {
                                    return null;
                                }
                                if (role === 'inherit' && !allowInherit) {
                                    return null;
                                }

                                const descriptor =
                                    role === null
                                        ? nullDescriptor
                                        : PERMISSION_ROLES_DESCRIPTORS[role];
                                const requireUpgrade = descriptor.needAdvancedPermissions
                                    ? upgradeForAdvancedPermissions
                                    : undefined;
                                return {
                                    ...(value !== undefined
                                        ? {
                                              type: PaletteCommandType.RADIO as const,
                                              selected: value === role,
                                          }
                                        : {
                                              selected: undefined,
                                          }),
                                    key: role || 'guest',
                                    icon: descriptor.icon,
                                    title: descriptor.title,
                                    subtitle: descriptor.description,
                                    badge:
                                        descriptor.needAdvancedPermissions && trialingPlan
                                            ? getTrialingBadgeProps({ plan: trialingPlan })
                                            : requireUpgrade
                                              ? {
                                                    kind: 'upgrade' as const,
                                                    label:
                                                        requireUpgrade.plan ?? requireUpgrade.label,
                                                    icon: Icon.Upgrade,
                                                }
                                              : undefined,
                                    trigger: () => {
                                        if (requireUpgrade) {
                                            requireUpgrade.onPress();
                                            return;
                                        }

                                        // @ts-ignore the value is accepted when allowInherit is passed
                                        onValueChange(role);
                                    },
                                };
                            })
                            .filter(filterOutNullable);

                        if (commands.length === 0) {
                            return null;
                        }

                        return {
                            key: group.key,
                            title: group.title,
                            commands,
                        } satisfies PaletteGroupDef;
                    })
                    .filter(filterOutNullable),
                onRemove
                    ? {
                          key: 'remove',
                          commands: [
                              {
                                  key: 'remove',
                                  title: removeLabel,
                                  icon: Icon.Trashcan,
                                  trigger: () => {
                                      if (onRemove) {
                                          onRemove();
                                      }
                                  },
                              },
                          ],
                      }
                    : null,
            ].filter(filterOutNullable),
        });
    };

    return (
        <PaletteButton
            leadingIcon={icon === null ? undefined : icon || currentDescriptor?.icon}
            trailingIcon={trailingIcon || Icon.ChevronDown}
            label={label || selectedRoleLabel || 'Select a role'}
            palette={palette}
            kind={buttonProps.kind ?? 'secondary'}
            {...buttonProps}
        />
    );
}
